import type { CustomerOrderDetail } from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { formatCount } from '@/shared/lib';
import { DesignThumbnail } from '@/shared/ui';

interface OrderSummaryProps {
  readonly order: CustomerOrderDetail;
  /** "견적 보기" 링크 대상 — 안전한 주소일 때만 위젯이 넘긴다. */
  readonly quoteUrl: string | null;
}

interface SummaryRow {
  readonly label: string;
  readonly value: ReactNode;
}

const LINK_CLASS = 'w-fit text-sm font-semibold underline underline-offset-4 hover:no-underline';

/**
 * 주문 정보 — 내 견적 상세의 요청 내용과 같은 모양(큰 상품명 아래 얇은 선으로 나눈 이름/값 줄). 주문일은 머리에 있다.
 * 상품명 옆에 견적 썸네일(없거나 못 불러오면 "미리보기 준비 중").
 */
export const OrderSummary = ({ order, quoteUrl }: OrderSummaryProps): ReactElement => {
  const quoteLink =
    quoteUrl === null ? null : quoteUrl.startsWith('/') ? (
      <Link to={quoteUrl} className={LINK_CLASS}>
        견적 보기
      </Link>
    ) : (
      <a href={quoteUrl} className={LINK_CLASS}>
        견적 보기
      </a>
    );
  const rows: SummaryRow[] = [
    {
      label: '수량',
      value: <span className="tabular-nums">{formatCount(order.quantity)}개</span>,
    },
    {
      label: '견적 번호',
      value: (
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="tabular-nums">{order.quoteNumber}</span>
          {quoteLink}
        </span>
      ),
    },
    ...(order.companyName !== null ? [{ label: '회사·단체명', value: order.companyName }] : []),
    { label: '담당자명', value: order.contactName },
  ];

  return (
    <section aria-label="주문 정보" className="flex flex-col gap-5">
      <div className="flex items-center gap-4 sm:gap-5">
        {/* 6rem(size-24) 아래로 줄이지 않는다 — 더 작으면 "미리보기 준비 중" 문구가 숨는다(DesignThumbnail @max-[6rem]). */}
        <DesignThumbnail
          url={order.thumbnailUrl}
          alt={`${order.productName} 디자인 미리보기`}
          className="size-24 shrink-0"
        />
        <div className="flex min-w-0 flex-col gap-3">
          <p
            aria-hidden
            className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
          >
            Order
          </p>
          <h2 className="text-[clamp(1.5rem,2.4vw,2rem)] leading-[1.2] font-bold tracking-[-0.02em] text-balance">
            {order.productName}
          </h2>
        </div>
      </div>
      <dl className="divide-y divide-foreground/10 border-t border-foreground/10 text-sm wrap-anywhere">
        {rows.map((row) => (
          <div key={row.label} className="flex gap-4 py-3">
            <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">{row.label}</dt>
            <dd className="min-w-0 flex-1">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};
